import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom'
import { SessionRefusee, useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import type { MessageKey } from '../i18n/messages'
import { LanguageToggle } from '../components/LanguageToggle'

/**
 * Les rubriques du panneau, et le nom que la barre supérieure porte pour chacune.
 *
 * L'ORDRE EST SIGNIFIANT : le plus spécifique d'abord. `/platform` préfixe toutes les
 * autres adresses du panneau ; placé en tête, il gagnerait toujours et la barre supérieure
 * dirait « Clients » jusque sur le formulaire de création.
 */
const SECTIONS: ReadonlyArray<{ chemin: string; cle: MessageKey }> = [
  { chemin: '/platform/plans/nouvelle', cle: 'platform.plans.new' },
  { chemin: '/platform/plans', cle: 'platform.nav.plans' },
  { chemin: '/platform/invoices', cle: 'platform.nav.invoices' },
  { chemin: '/platform/dashboard', cle: 'growth.dash.title' },
  { chemin: '/platform/discounts', cle: 'growth.codes.title' },
  { chemin: '/platform/requests', cle: 'growth.requests.title' },
  { chemin: '/platform/leads', cle: 'growth.leads.title' },
  { chemin: '/platform/billing', cle: 'billing.seller.title' },
  { chemin: '/platform/clients/nouveau', cle: 'platform.clients.new' },
  { chemin: '/platform', cle: 'platform.nav.clients' },
]

/**
 * Coquille du panneau plateforme.
 *
 * Elle vit ici et non dans App.tsx, qui réunit déjà la coquille client, sa navigation et
 * une soixantaine de routes : y ajouter un second univers rendrait le fichier illisible.
 *
 * Le masquage fait ici est un CONFORT d'affichage, jamais un contrôle : la garde réelle
 * est le filtre RequirePlatformAdmin côté API, et la frontière est le déclencheur
 * tg_app_user_platform_role en base.
 *
 * Elle n'est montée que par le portail d'administration (src/admin), application à part :
 * une session d'un autre rôle n'y a rien à faire et repart à la connexion — il n'y a pas
 * d'écrans métier vers lesquels la renvoyer dans ce bundle-là.
 *
 * TROIS TITRES DIFFÉRENTS, ET C'EST LE CORRECTIF. La marque de la barre latérale nomme le
 * PANNEAU, la barre supérieure nomme la SECTION ouverte, la page nomme ce qu'elle montre.
 * Jusqu'ici les trois affichaient `platform.title` : « Panneau plateforme » apparaissait
 * trois fois sur le même écran, et le surtitre « Accueil » contredisait le titre juste en
 * dessous. Répéter le même mot à trois places n'apprend rien et masque où l'on se trouve.
 */
export function PlatformShell() {
  const { session, signOut } = useAuth()
  const { t, direction } = useI18n()
  const loc = useLocation()

  if (!session) return <Navigate to="/login" replace />
  if (session.role !== 'platform_admin') {
    return <SessionRefusee />
  }

  const isOn = (p: string) => loc.pathname === p || loc.pathname.startsWith(p + '/')
  const isRtl = direction === 'rtl'
  const section = SECTIONS.find((s) => isOn(s.chemin))?.cle ?? 'platform.nav.clients'

  return (
    <div className="app" dir={isRtl ? 'rtl' : 'ltr'} style={isRtl ? { flexDirection: 'row-reverse' } : undefined}>
      <aside className="side" style={isRtl ? { order: 2 } : undefined}>
        <div className="brand">
          <span className="mark">
            <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </span>
          <span>
            <b>{t('platform.title')}</b>
            <span className="tag">{t('app.name')}</span>
          </span>
        </div>

        <div className="navgroup">
          <nav className="nav">
            {/* « Clients » couvre tout /platform SAUF les formules et la facturation : sans
                l'exclusion, deux entrées seraient allumées ensemble. */}
            <NavLink to="/platform/dashboard" className={isOn('/platform/dashboard') ? 'on' : ''}>
              <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
              </svg>
              <span>{t('growth.dash.title')}</span>
            </NavLink>
            <NavLink
              to="/platform"
              className={
                isOn('/platform') &&
                !['/platform/plans', '/platform/invoices', '/platform/billing', '/platform/dashboard', '/platform/discounts', '/platform/requests', '/platform/leads'].some(isOn)
                  ? 'on'
                  : ''
              }
            >
              <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 21h18" />
                <path d="M5 21V7l7-4 7 4v14" />
                <path d="M9 9h2M9 13h2M9 17h2M14 9h1M14 13h1M14 17h1" />
              </svg>
              <span>{t('platform.nav.clients')}</span>
            </NavLink>
            <NavLink to="/platform/plans" className={isOn('/platform/plans') ? 'on' : ''}>
              <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h10" />
              </svg>
              <span>{t('platform.nav.plans')}</span>
            </NavLink>
            <NavLink to="/platform/invoices" className={isOn('/platform/invoices') || isOn('/platform/billing') ? 'on' : ''}>
              <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 3h9l3 3v15l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21z" />
                <path d="M9 9h6M9 13h6M9 17h3" />
              </svg>
              <span>{t('platform.nav.invoices')}</span>
            </NavLink>
            <NavLink to="/platform/discounts" className={isOn('/platform/discounts') ? 'on' : ''}>
              <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M19 5 5 19M7 7h.01M17 17h.01" />
                <circle cx="7" cy="7" r="2.5" />
                <circle cx="17" cy="17" r="2.5" />
              </svg>
              <span>{t('growth.codes.title')}</span>
            </NavLink>
            <NavLink to="/platform/requests" className={isOn('/platform/requests') ? 'on' : ''}>
              <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 5h16v11H8l-4 4z" />
              </svg>
              <span>{t('growth.requests.title')}</span>
            </NavLink>
            <NavLink to="/platform/leads" className={isOn('/platform/leads') ? 'on' : ''}>
              <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 6 12 13 2 6" />
                <path d="M2 6h20v12H2z" />
              </svg>
              <span>{t('growth.leads.title')}</span>
            </NavLink>
          </nav>
        </div>

        <div className="sidefoot">{session.fullName}</div>
      </aside>

      <div className="main" style={isRtl ? { order: 1 } : undefined}>
        <header className="topbar">
          <span className="sel">
            <b>{t(section)}</b>
          </span>
          <div className="spacer" />
          <LanguageToggle compact />
          <span className="avatar" title={session.fullName}>
            {session.fullName.slice(0, 2).toUpperCase()}
          </span>
          <button type="button" className="tbtn" onClick={signOut}>
            {t('nav.signOut')}
          </button>
        </header>
        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
